import { useEffect, useRef } from 'react'

interface LevelMeterProps {
  analyser: AnalyserNode | null
  label: string
  icon: string
}

// Layout: 24 segments  ▌green×16 ▌yellow×4 ▌red×4
const SEGMENTS    = 24
const SEG_YELLOW  = 16
const SEG_RED     = 20

function drawInactive(ctx: CanvasRenderingContext2D, W: number, H: number) {
  ctx.clearRect(0, 0, W, H)
  const segW = W / SEGMENTS
  for (let i = 0; i < SEGMENTS; i++) {
    ctx.fillStyle = i < SEG_YELLOW ? '#0d1f0d' : i < SEG_RED ? '#1a1600' : '#1f0808'
    ctx.fillRect(i * segW + 0.5, 0.5, segW - 1, H - 1)
  }
}

export default function LevelMeter({ analyser, label, icon }: LevelMeterProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    const W = canvas.width
    const H = canvas.height

    if (!analyser) {
      drawInactive(ctx, W, H)
      return
    }

    const bufferLength = analyser.frequencyBinCount
    const dataArray    = new Uint8Array(bufferLength)
    let rafId   = 0
    let peak    = 0
    let peakHold = 0

    function draw() {
      rafId = requestAnimationFrame(draw)
      analyser!.getByteTimeDomainData(dataArray)

      // RMS amplitude → 0..1
      let sum = 0
      for (let i = 0; i < bufferLength; i++) {
        const v = (dataArray[i] / 128.0) - 1.0
        sum += v * v
      }
      const level = Math.min(1, Math.sqrt(sum / bufferLength) * 6)  // ×6 so normal voice ≈ 60–80%

      // Peak hold / slow decay
      if (level >= peak) {
        peak     = level
        peakHold = 45          // hold ~0.75 s @ 60 fps
      } else {
        if (peakHold > 0) peakHold--
        else peak = Math.max(0, peak - 0.015)
      }

      const litCount = Math.round(level * SEGMENTS)
      const peakSeg  = Math.min(SEGMENTS - 1, Math.round(peak * SEGMENTS) - 1)
      const segW     = W / SEGMENTS

      ctx.clearRect(0, 0, W, H)

      for (let i = 0; i < SEGMENTS; i++) {
        const lit = i < litCount
        if (i < SEG_YELLOW)     ctx.fillStyle = lit ? '#22c55e' : '#0d1f0d'
        else if (i < SEG_RED)   ctx.fillStyle = lit ? '#eab308' : '#1a1600'
        else                    ctx.fillStyle = lit ? '#ef4444' : '#1f0808'
        ctx.fillRect(i * segW + 0.5, 0.5, segW - 1, H - 1)
      }

      // Peak-hold marker – bright white flash on that segment
      if (peakSeg >= 0 && peak > 0.02) {
        ctx.fillStyle = 'rgba(255,255,255,0.9)'
        ctx.fillRect(peakSeg * segW + 0.5, 0.5, segW - 1, H - 1)
      }
    }

    draw()
    return () => cancelAnimationFrame(rafId)
  }, [analyser])

  return (
    <div className="level-meter">
      <span className="level-meter__icon">{icon}</span>
      <span className="level-meter__label">{label}</span>
      <canvas ref={canvasRef} className="level-meter__canvas" width={280} height={16} />
    </div>
  )
}
